import type { MentionQuery } from "@/shared/api/api-client";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { formatDateTime } from "@/shared/utils/format";
import type { Mention } from "@/shared/types/mention";

/** Espelho de `core/mention_trash.py::TRASH_WINDOW`; a paridade é travada
 *  por `tests/test_trash_window_parity.py`. */
export const TRASH_WINDOW_DAYS = 15;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Dias inteiros que faltam pra menção sair da lixeira (nunca negativo). */
export function daysLeftInTrash(discardedAt: string, now: Date): number {
  const discarded = parseServerTime(discardedAt);
  if (discarded === null) return 0;
  const leftMs = discarded + TRASH_WINDOW_DAYS * DAY_MS - now.getTime();
  return Math.max(0, Math.ceil(leftMs / DAY_MS));
}

export function trashExitLabel(days: number): string {
  if (days <= 0) return "Sai da lixeira hoje";
  if (days === 1) return "Sai da lixeira em 1 dia";
  return `Sai da lixeira em ${days} dias`;
}

/** "Descartada por NOME em DD/MM/AAAA HH:MM · motivo". */
export function discardedByLabel(mention: Mention): string {
  const parsed = parseServerTime(mention.discardedAt);
  const when = parsed === null ? "" : ` em ${formatDateTime(new Date(parsed))}`;
  const who = mention.discardedByName ? ` por ${mention.discardedByName}` : "";
  const reason = mention.discardReason ? ` · ${mention.discardReason}` : "";
  return `Descartada${who}${when}${reason}`;
}

/** DONO da consulta da lixeira: a lista, a contagem e o alvo do lote por
 *  filtro usam a mesma. */
export function trashListQuery(input: {
  page: number;
  size: number;
  q: string;
  brandIds: number[];
}): MentionQuery {
  return {
    page: input.page,
    size: input.size,
    order: "discarded_desc",
    scope: "all",
    inTrash: true,
    q: input.q || undefined,
    brandIds: input.brandIds.length ? input.brandIds : undefined
  };
}

/** O texto da linha: o título, ou o TRECHO quando o post não tem título
 *  (rede). `toMention` troca título nulo por "Publicação sem título", e é
 *  por essa marca que a lista de menções decide subir o trecho — a mesma
 *  regra aqui, senão toda linha de rede viraria "Publicação sem título". */
export function trashRowTitle(mention: Pick<Mention, "title" | "excerpt">): string {
  const untitled = !mention.title || mention.title === "Publicação sem título";
  return untitled ? mention.excerpt || mention.title : mention.title;
}
